import { createContext, useCallback, useContext, useEffect, useId, useState } from "react";

export interface FabAction {
  id: string;
  onClick: () => void;
  icon: React.ReactNode;
  label: string;
  /** Lower number = closer to FAB (inner ring). Default: 0 */
  priority?: number;
}

interface FabActionsContextValue {
  actions: FabAction[];
  register: (action: FabAction) => void;
  unregister: (id: string) => void;
}

const FabActionsContext = createContext<FabActionsContextValue>({
  actions: [],
  register: () => {},
  unregister: () => {},
});

export function FabActionsProvider({ children }: { children: React.ReactNode }) {
  const [actions, setActions] = useState<FabAction[]>([]);

  const register = useCallback((action: FabAction) => {
    setActions((prev) => {
      // Replace if same id already exists, otherwise add
      const filtered = prev.filter((a) => a.id !== action.id);
      return [...filtered, action].sort(
        (a, b) => (a.priority ?? 0) - (b.priority ?? 0)
      );
    });
  }, []);

  const unregister = useCallback((id: string) => {
    setActions((prev) => prev.filter((a) => a.id !== id));
  }, []);

  return (
    <FabActionsContext.Provider value={{ actions, register, unregister }}>
      {children}
    </FabActionsContext.Provider>
  );
}

/**
 * Register a FAB action that lives as long as the calling component is mounted.
 *
 * Usage in any page:
 * ```tsx
 * useRegisterFabAction({
 *   label: "Ny aktivitet",
 *   icon: <AddTaskIcon />,
 *   onClick: () => setOpen(true),
 * });
 * ```
 */
export function useRegisterFabAction(
  action: Omit<FabAction, "id"> & { id?: string }
) {
  const reactId = useId();
  const id = action.id ?? reactId;
  const { register, unregister } = useContext(FabActionsContext);

  useEffect(() => {
    register({ ...action, id });
    return () => unregister(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, action.label, action.priority]);
}

export function useFabActions() {
  return useContext(FabActionsContext).actions;
}
